// Global Axios HTTP Client
// Handles interceptors, auth headers, token refresh, and error handling

import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import { getClientTimezoneOffsetMinutes } from '@/composables/useDateFormat';
import { errorService } from '@/core/error/errorService';
import { authEndpoints } from '@/services/api/apiConfig';
import { useAuthStore } from '@/domains/auth/stores/useAuthStore';
import { startRequest, endRequest } from '@/core/loading/globalLoadingStore';
import router from '@/router';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

// Global flag to prevent infinite retry loops when refresh fails
let refreshFailed = false;

// Function to reset the refresh failed flag (call after successful login)
export const resetRefreshFailedFlag = () => {
  refreshFailed = false;
};

const getLoginUrl = async (): Promise<string> => {
  try {
    const storedGuid = localStorage.getItem('userGuid');
    if (storedGuid) {
      const route = router.resolve({
        name: 'auth-verify',
        params: { uuid: storedGuid },
        query: { noAutoRequest: 'true' },
      });
      return route.href;
    }
  } catch (error) {
    console.warn('[axios] Error getting stored UUID:', error);
  }

  // No stored UUID, redirect to home
  const homeRoute = router.resolve({ name: 'home' });
  return homeRoute.href;
};

const isAuthRoute = (): boolean => {
  const path = globalThis.location.pathname.toLowerCase();
  return path.startsWith('/auth/') || path.startsWith('/login') || path === '/';
};

const valueToString = (value: unknown): string => {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
    return String(value);
  }
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    return JSON.stringify(value);
  }
  // Fallback for symbols only (null, undefined, arrays handled separately by caller)
  if (typeof value === 'symbol') {
    return value.toString();
  }
  // This should never be reached, but TypeScript requires a return
  return String(value);
};

// Custom params serializer for ASP.NET Core array parameter format
const paramsSerializer = (params: Record<string, unknown>): string => {
  const searchParams = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (Array.isArray(value)) {
      // ASP.NET Core format: name=value1&name=value2
      for (const item of value) {
        searchParams.append(key, valueToString(item));
      }
    } else if (value !== null && value !== undefined) {
      searchParams.append(key, valueToString(value));
    }
  }

  return searchParams.toString();
};

// Create axios instance (Singleton Pattern)
const axiosInstance: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 90000, // 1.5 minutes
  headers: {
    'Content-Type': 'application/json',
  },
  paramsSerializer,
});

// Request Interceptor - Add Bearer token and device ID
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    startRequest();

    // Add Bearer token if available
    const authStore = useAuthStore();
    if (authStore.state.accessToken && config.headers) {
      config.headers['Authorization'] = `Bearer ${authStore.state.accessToken}`;
    }

    // Add device ID if available
    const deviceId = localStorage.getItem('deviceId');
    if (deviceId && config.headers) {
      config.headers['X-Device-ID'] = deviceId;
    }

    // Add client timezone offset (minutes from UTC) for server-side local time conversion
    if (config.headers) {
      config.headers['X-Timezone-Offset'] = String(getClientTimezoneOffsetMinutes());
    }

    return config;
  },
  (error: AxiosError) => {
    errorService.handleError(error, 'axios-request');
    endRequest();
    throw error;
  }
);

// Response Interceptor - Handle errors and authentication
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    endRequest();
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Handle 401 Unauthorized - Attempt token refresh
    // Skip refresh logic if the failed request IS the refresh endpoint itself to prevent infinite loops
    const isRefreshEndpoint = originalRequest?.url?.includes(authEndpoints.REFRESH());

    // If refresh already failed, don't retry - just redirect to login
    if (refreshFailed && error.response?.status === 401) {
      if (!isAuthRoute()) {
        const loginUrl = await getLoginUrl();
        globalThis.location.href = loginUrl;
      }
      endRequest();
      throw error;
    }

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !isRefreshEndpoint &&
      !refreshFailed
    ) {
      originalRequest._retry = true;

      try {
        // Set reconnecting state to show loader
        const authStore = useAuthStore();
        authStore.actions.setReconnecting(true);

        const refreshToken = authStore.actions.getRefreshToken();
        if (!refreshToken) {
          throw new Error('No refresh token available');
        }

        const refreshResponse = await axiosInstance.post<{
          accessToken: string;
          refreshToken: string;
          user: {
            userAccountId: number;
            name: string;
            lastName: string;
            brokerCode?: string;
          };
        }>(authEndpoints.REFRESH(), {
          refreshToken,
        });

        authStore.actions.login({
          accessToken: refreshResponse.data.accessToken,
          refreshToken: refreshResponse.data.refreshToken,
          user: refreshResponse.data.user,
        });

        // Clear reconnecting state
        authStore.actions.setReconnecting(false);

        // Retry the original request with new access token
        const retryResponse = await axiosInstance(originalRequest);
        endRequest();
        return retryResponse;
      } catch {
        // Mark refresh as failed to prevent infinite retries
        refreshFailed = true;

        // Clear reconnecting state on failure
        const authStore = useAuthStore();
        authStore.actions.setReconnecting(false);

        // Refresh token expired - this is expected behavior, not an error
        if (import.meta.env.DEV) {
          console.info('[Auth] Refresh token expired, redirecting to login');
        }

        authStore.actions.logout();

        // Refresh failed - session truly expired, redirect to login with stored UUID
        // Only redirect if not already on an auth route (avoid redirect loops)
        if (!isAuthRoute()) {
          const loginUrl = await getLoginUrl();
          globalThis.location.href = loginUrl;
        }
        endRequest();
        throw error;
      }
    }

    // If refresh endpoint itself returns 401, mark as failed and redirect to login immediately
    if (error.response?.status === 401 && isRefreshEndpoint) {
      refreshFailed = true;

      const authStore = useAuthStore();
      authStore.actions.setReconnecting(false);

      authStore.actions.logout();

      // Refresh token expired - this is expected behavior, not an error
      // Log to console for debugging but don't treat as error (no error service, no notification)
      if (import.meta.env.DEV) {
        console.info('[Auth] Refresh token expired, redirecting to login');
      }

      // Only redirect if not already on an auth route (avoid redirect loops)
      if (!isAuthRoute()) {
        const loginUrl = await getLoginUrl();
        globalThis.location.href = loginUrl;
      }
      endRequest();
      throw error;
    }

    if (!error.response) {
      console.error('[Axios] Network error', error);
      endRequest();
      throw error;
    }

    // Don't log 4xx errors (client errors) - these are expected business logic errors
    // Only log 5xx errors (server errors) which indicate unexpected problems
    if (error.response.status >= 500) {
      errorService.handleError(error, 'axios');
    }

    endRequest();
    throw error;
  }
);

export default axiosInstance;
